
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock as LockIcon, FileText, Link as LinkIcon, CheckCircle2, Download, Loader2, AlertTriangle, MessageCircle, ExternalLink, Clock, DollarSign, Folder, X } from 'lucide-react';
import { lockService } from '../services/lockService';
import { Lock, LockType } from '../types';
import { Button } from './ui/Button';

type VisitorStatus = 'idle' | 'loading' | 'awaiting_engagement' | 'verifying' | 'success' | 'error' | 'tipping';

const VisitorLock: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [lock, setLock] = useState<Lock | null>(null);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<VisitorStatus>('idle');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tooFast, setTooFast] = useState(false);
  const [b2bError, setB2bError] = useState<string | null>(null);

  // Refs for Psychological Gating & Counting
  const leftAtRef = useRef<number | null>(null);
  const hasCountedViewRef = useRef(false);

  useEffect(() => {
    if (slug) {
      lockService.getLockBySlug(slug)
        .then(data => {
            if (data) {
                setLock(data);
                // Reliable View Counting: Only increment once per component mount/session
                // This protects against React Strict Mode double-invocation
                if (!hasCountedViewRef.current) {
                    hasCountedViewRef.current = true;
                    lockService.incrementView(data.id);
                }
            } else {
                setNotFound(true);
            }
        })
        .catch(() => setNotFound(true))
        .finally(() => setLoading(false));
    }
  }, [slug]);

  // Logic for Psychological Gate (Time spent away)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (status === 'awaiting_engagement' && document.visibilityState === 'visible') {
        // User returned to tab
        if (leftAtRef.current) {
          const timeAway = Date.now() - leftAtRef.current;
          const MIN_TIME_AWAY_MS = 8000; 

          setTooFast(false);
          
          if (timeAway < MIN_TIME_AWAY_MS) {
            setTooFast(true);
            leftAtRef.current = null; // Reset to force them to click again
          } else {
            // Success - pretend to verify with backend
            setStatus('verifying');
            
            // 2 seconds of "Verifying..." spinner for perceived security
            setTimeout(() => {
                unlockContent();
            }, 2000); 
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [status]);

  const unlockContent = async () => {
    if (!lock) return;
    
    // If file, get signed url now
    if (lock.type === LockType.FILE) {
         try {
            const url = await lockService.getFileDownloadUrl(lock.targetUrl);
            setDownloadUrl(url);
            setStatus('success');
         } catch (e) {
            setStatus('error');
         }
    } else if (lock.type === LockType.LINK) {
        setStatus('success');
        setTimeout(() => {
             let url = lock.targetUrl;
             if (!url.startsWith('http')) url = 'https://' + url;
             window.location.href = url;
        }, 1500);
    }
  };

  const isPublicEmail = (email: string) => {
      const domains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com', 'icloud.com', 'aol.com'];
      const domain = email.split('@')[1]?.toLowerCase();
      return domains.includes(domain);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lock || !email) return;
    
    // B2B Guard - SAFE ACCESS
    if (lock.config?.security?.b2bOnly && isPublicEmail(email)) {
        setB2bError("This content is for professionals only. Please use your work email.");
        return;
    }
    setB2bError(null);

    setStatus('loading');
    
    try {
        await lockService.captureLead(lock.id, email);
        
        // Decision Tree: Tips -> Social -> Direct
        if (lock.config?.tip?.enabled) {
            setStatus('tipping');
        } else if (lock.socialUrl && lock.socialUrl.trim().length > 0) {
            setStatus('awaiting_engagement');
        } else {
            unlockContent();
        }

    } catch (error) {
        console.error(error);
        setStatus('error');
    }
  };

  const handleTipSuccess = () => {
      // Simulate payment processing
      setStatus('loading'); 
      setTimeout(() => {
          if (lock?.socialUrl && lock.socialUrl.trim().length > 0) {
              setStatus('awaiting_engagement');
          } else {
              unlockContent();
          }
      }, 1500);
  };

  const handleSocialAction = () => {
      if (!lock?.socialUrl) return;
      setTooFast(false);
      leftAtRef.current = Date.now();
      
      let url = lock.socialUrl;
      if (!url.startsWith('http')) url = 'https://' + url;
      window.open(url, '_blank');
  };

  const handleDownload = () => {
      if (downloadUrl) {
          window.open(downloadUrl, '_blank');
      }
  };

  // Safe Defaults
  const blurAmount = lock?.config?.preview?.blurStrength === 'none' ? 'blur(0px)' : 'blur(8px)';
  const showFomo = lock?.config?.fomo?.enabled;
  const fomoText = lock?.config?.fomo?.text || "Limited Time";
  const tipAmount = lock?.config?.tip?.amount || 3.00;
  const isFolder = lock?.config?.files?.isFolder;
  const b2bPlaceholder = lock?.config?.security?.b2bOnly ? "name@company.com" : "name@example.com";

  // Dynamic OG Image: Use the lock's background image if available, otherwise default to the new branding
  const ogImage = lock?.imageUrl || 'https://images.unsplash.com/photo-1639322537228-f710d846310a?q=80&w=2532';

  // Dynamic SEO Management
  useEffect(() => {
      if (notFound) {
          document.title = 'Lock Not Found | Lockd.in';
      } else if (lock) {
          document.title = `${lock.title} | Lockd.in`;
          
          // Helper to safely update meta tags
          const updateMeta = (selector: string, attribute: string, value: string, createName?: string, createProp?: string) => {
              let el = document.querySelector(selector);
              if (!el) {
                  el = document.createElement('meta');
                  if (createName) el.setAttribute('name', createName);
                  if (createProp) el.setAttribute('property', createProp);
                  document.head.appendChild(el);
              }
              el.setAttribute(attribute, value);
          };

          updateMeta('meta[name="description"]', 'content', "Exclusive content protected by Lockd.in. Enter your email to unlock.", 'description');
          
          // OG
          updateMeta('meta[property="og:title"]', 'content', lock.title, undefined, 'og:title');
          updateMeta('meta[property="og:description"]', 'content', "Exclusive content. Unlock to view.", undefined, 'og:description');
          
          // Ensure absolute URL for OG Image to prevent preview bugs
          let absoluteOgImage = ogImage;
          if (absoluteOgImage && !absoluteOgImage.startsWith('http')) {
             // Handle relative paths if necessary (though usually we store full URLs or signed URLs)
             // For now, we assume if it's not http, it might be a supabase path, but usually we want a full url.
             // If it's really broken, fallback to default.
             absoluteOgImage = 'https://images.unsplash.com/photo-1639322537228-f710d846310a?q=80&w=2532';
          }
          updateMeta('meta[property="og:image"]', 'content', absoluteOgImage, undefined, 'og:image');
          
          // Note: We do NOT update og:url here because hashed URLs often break scrapers. 
          // Leaving the default (origin) often provides a better fallback experience (Landing Page preview)
          // than a broken link preview.
          
          // Twitter
          updateMeta('meta[name="twitter:title"]', 'content', lock.title, 'twitter:title');
          updateMeta('meta[name="twitter:description"]', 'content', "Exclusive content. Unlock to view.", 'twitter:description');
          updateMeta('meta[name="twitter:image"]', 'content', absoluteOgImage, 'twitter:image');
      }
  }, [lock, notFound, ogImage]);


  if (loading) {
      return <div className="min-h-[100dvh] bg-slate-900 flex items-center justify-center"><Loader2 className="text-white animate-spin" /></div>;
  }

  if (notFound) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-slate-50 p-6 text-center font-sans">
        <div>
            <div className="mx-auto w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center mb-4">
                <LockIcon className="h-8 w-8 text-slate-500" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Lock Not Found</h1>
            <p className="text-slate-500">This link may have expired or is invalid.</p>
        </div>
      </div>
    );
  }

  if (!lock) return null;

  return (
    <div className="relative min-h-[100dvh] w-full overflow-hidden bg-slate-900 flex flex-col items-center justify-center font-sans p-4">
      
      {/* Background Image with Configurable Blur */}
      <div 
        className="absolute inset-0 z-0 bg-cover bg-center transition-opacity duration-1000"
        style={{ 
            backgroundImage: `url(${lock.imageUrl || 'https://images.unsplash.com/photo-1550684848-fac1c5b4e853?q=80&w=2070'})`,
            opacity: 0.6,
            filter: blurAmount
        }}
      />
      <div className="absolute inset-0 z-0 bg-slate-900/40 backdrop-blur-md" />

      {/* Main Card */}
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
        className="relative z-10 w-full max-w-md bg-white/90 backdrop-blur-2xl rounded-[32px] p-6 sm:p-8 shadow-2xl border border-white/50"
      >
        {/* Icon Header */}
        <div className="flex justify-center -mt-12 sm:-mt-14 mb-4">
            <div className="bg-white p-4 rounded-full shadow-xl ring-4 ring-black/5">
                {isFolder ? (
                    <Folder className="h-8 w-8 text-blue-600" />
                ) : lock.type === LockType.FILE ? (
                    <FileText className="h-8 w-8 text-slate-900" />
                ) : (
                    <LinkIcon className="h-8 w-8 text-slate-900" />
                )}
            </div>
        </div>

        {/* FOMO Banner - Integrated below icon */}
        {showFomo && (
             <div className="flex justify-center mb-4">
                 <div className="bg-red-50 text-red-600 px-3 py-1 rounded-full text-xs font-bold border border-red-100 flex items-center gap-1.5 animate-pulse">
                    <Clock className="w-3.5 h-3.5" />
                    {fomoText}
                 </div>
             </div>
        )}

        <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-slate-900 mb-2 tracking-tight leading-tight">
                {lock.title}
            </h1>
            <p className="text-slate-500 text-sm">
                {status === 'awaiting_engagement' 
                    ? "Action required to unlock" 
                    : status === 'tipping'
                    ? "Support the creator"
                    : "Enter your email to unlock this content."
                }
            </p>
        </div>

        <AnimatePresence mode='wait'>
            
            {/* SUCCESS STATE */}
            {status === 'success' && (
                <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-8"
                >
                    <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                        <CheckCircle2 className="h-8 w-8 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">Access Granted!</h3>
                    <p className="text-slate-500 text-sm mb-6">
                        {lock.type === LockType.LINK ? "Redirecting you now..." : "Your content is ready."}
                    </p>
                    
                    {lock.type === LockType.FILE ? (
                        <Button variant="primary" fullWidth onClick={handleDownload}>
                            <Download className="mr-2 h-4 w-4" /> 
                            {isFolder ? 'Download All Files' : 'Download File'}
                        </Button>
                    ) : (
                        <div className="flex justify-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div>
                        </div>
                    )}
                </motion.div>
            )}

            {/* TIPPING STATE (Mock) */}
            {status === 'tipping' && (
                <motion.div
                    key="tipping"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-6"
                >
                     <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 text-center">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <DollarSign className="w-5 h-5 text-green-600" />
                        </div>
                        <h3 className="font-semibold text-slate-900 mb-1">Say thanks with a Tip</h3>
                        <p className="text-sm text-slate-500 mb-4">
                             Your support helps me create more content like this.
                        </p>
                        
                        <button 
                            onClick={handleTipSuccess}
                            className="w-full h-12 bg-black text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform"
                        >
                            Pay ${tipAmount.toFixed(2)} <span className="bg-white/20 text-[10px] px-1.5 rounded">Apple Pay</span>
                        </button>
                        
                        <button 
                            onClick={handleTipSuccess} // Skip for demo
                            className="mt-3 text-xs text-slate-400 hover:text-slate-600 underline"
                        >
                            No thanks, just unlock
                        </button>
                     </div>
                </motion.div>
            )}

            {/* SOCIAL GATE STATE */}
            {(status === 'awaiting_engagement' || status === 'verifying') && (
                <motion.div
                    key="social"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-6"
                >
                    {status === 'verifying' ? (
                        <div className="py-12 flex flex-col items-center justify-center text-center">
                            <Loader2 className="w-10 h-10 text-slate-900 animate-spin mb-4" />
                            <h3 className="text-lg font-semibold text-slate-900">Verifying engagement...</h3>
                            <p className="text-slate-500 text-sm">Please wait a moment.</p>
                        </div>
                    ) : (
                        <>
                            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 text-center">
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <MessageCircle className="w-5 h-5 text-blue-600" />
                                </div>
                                <h3 className="font-semibold text-slate-900 mb-1">Comment to Unlock</h3>
                                <p className="text-sm text-slate-500 mb-4">
                                    Support the creator! Go to the post and drop a quick comment to unlock the link immediately.
                                </p>
                                
                                {tooFast && (
                                    <motion.div 
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="mb-4 p-3 bg-red-50 text-red-600 text-xs rounded-xl flex items-center justify-center gap-2"
                                    >
                                        <Clock className="w-3 h-3" />
                                        <span>Too fast! Did you really comment?</span>
                                    </motion.div>
                                )}

                                <Button 
                                    onClick={handleSocialAction}
                                    fullWidth 
                                    className="h-12 bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/20"
                                >
                                    Go to Post & Comment <ExternalLink className="w-4 h-4 ml-2" />
                                </Button>
                            </div>
                            <p className="text-xs text-center text-slate-400">
                                Return to this tab after commenting.
                            </p>
                        </>
                    )}
                </motion.div>
            )}

            {/* EMAIL CAPTURE STATE */}
            {(status === 'idle' || status === 'loading' || status === 'error') && (
                <motion.form
                    key="form"
                    initial={{ opacity: 1 }}
                    exit={{ opacity: 0, x: -20 }}
                    onSubmit={handleEmailSubmit}
                    className="space-y-4"
                >
                    {status === 'error' && (
                        <div className="p-3 bg-red-50 text-red-600 text-sm rounded-xl flex items-center">
                            <AlertTriangle className="w-4 h-4 mr-2" /> Something went wrong.
                        </div>
                    )}

                    <div className="space-y-2">
                        <label htmlFor="email" className="sr-only">Email Address</label>
                        <div className="relative">
                            <input
                                type="email"
                                id="email"
                                required
                                placeholder={b2bPlaceholder}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className={`w-full h-14 px-5 rounded-2xl bg-slate-100 border-2 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none transition-colors text-lg ${b2bError ? 'border-red-200 focus:border-red-500' : 'border-transparent focus:border-slate-900'}`}
                            />
                        </div>
                        {b2bError && (
                            <p className="text-xs text-red-500 flex items-center ml-1">
                                <AlertTriangle className="w-3 h-3 mr-1" /> {b2bError}
                            </p>
                        )}
                    </div>

                    <Button 
                        type="submit" 
                        fullWidth 
                        isLoading={status === 'loading'}
                        className="h-14 text-lg shadow-lg shadow-slate-900/20"
                    >
                        {lock.buttonText}
                    </Button>
                    
                    <p className="text-xs text-center text-slate-400 mt-4 flex items-center justify-center">
                        <LockIcon className="w-3 h-3 mr-1.5" />
                        Secure & encrypted delivery
                    </p>
                </motion.form>
            )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default VisitorLock;